// sttClient.js
// Exports functions to start/stop streaming microphone audio to the server STT WS
// Sends messages as JSON:
// { event: "start" }
// { event: "audio", audioChunk: "<base64>" }
// { event: "stop" }

let audioContext = null;
let processor = null;
let source = null;
let socket = null;
let isStreaming = false;
let mediaStream = null;

function floatTo16BitPCM(float32Array) {
  const l = float32Array.length;
  const buffer = new ArrayBuffer(l * 2);
  const view = new DataView(buffer);
  let offset = 0;
  for (let i = 0; i < l; i++, offset += 2) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

function encodeBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

// CHANGED: downsample audio to 16kHz for better STT stability/accuracy
function downsampleBuffer(buffer, inputSampleRate, outputSampleRate) {
  if (outputSampleRate === inputSampleRate) return buffer;
  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0, count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = accum / count;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

// Tracks the latest transcription messages so stopSTT() can return the complete
// final text even if the server's last chunk arrives slightly after "stop".
let lastFinalText = "";
let lastPartialText = "";
let pendingFinalResolve = null;
let pendingFinalTimer = null;

export async function startSTT(wsUrl, options, onTranscription) {
  if (isStreaming) return;
  isStreaming = true;
  lastFinalText = "";
  lastPartialText = "";

  // open websocket
  socket = new WebSocket(wsUrl);
  socket.binaryType = "arraybuffer";

  socket.onopen = () => {
    console.log("STT WS open");
    socket.send(JSON.stringify({ event: "start", language: options.language }));
  };

  socket.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg.type === "stt") {
        // Accumulate final text; keep the latest partial separately.
        if (msg.isPartial) {
          lastPartialText = msg.text || "";
        } else {
          lastFinalText = (lastFinalText + " " + (msg.text || "")).trim();
          lastPartialText = "";
        }
        if (onTranscription) onTranscription({ text: msg.text, isPartial: msg.isPartial });
        // If we're waiting to stop and a FINAL result just arrived, finalize now.
        if (pendingFinalResolve && !msg.isPartial) {
          const r = pendingFinalResolve;
          pendingFinalResolve = null;
          if (pendingFinalTimer) { clearTimeout(pendingFinalTimer); pendingFinalTimer = null; }
          r();
        }
      }
    } catch (e) {
      // fallback if server sends plain text
      if (onTranscription) onTranscription(ev.data);
    }
  };

  socket.onclose = () => console.log("STT WS closed");

  // get mic
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  mediaStream = stream;

  // CHANGED: let browser choose native sample rate; we downsample ourselves to 16k
  audioContext = new (window.AudioContext || window.webkitAudioContext)();
  source = audioContext.createMediaStreamSource(stream);

  // Use ScriptProcessorNode to get raw PCM (fallback; AudioWorklet recommended but more code)
  const bufferSize = 4096;
  processor = audioContext.createScriptProcessor(bufferSize, 1, 1);

  processor.onaudioprocess = (e) => {
    if (!isStreaming) return;
    const channelData = e.inputBuffer.getChannelData(0); // mono

    // CHANGED: downsample to 16k before encoding to PCM16
    const inputRate = audioContext.sampleRate;
    const downsampled = downsampleBuffer(channelData, inputRate, 16000);
    const pcm16Buffer = floatTo16BitPCM(downsampled);
    const base64Chunk = encodeBase64(pcm16Buffer);

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ event: "audio", audioChunk: base64Chunk }));
    }
  };

  source.connect(processor);
  processor.connect(audioContext.destination);
}

function buildCombinedTranscript() {
  return [lastFinalText, lastPartialText]
    .map((t) => (t || "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

// Stop streaming. Returns a Promise that resolves with the COMPLETE transcript
// (final + any trailing partial) once the server sends its final result, or after
// a short grace window — so the caller never reads a half-finished transcript.
// This makes mic-Stop reliable regardless of network timing.
export function stopSTT() {
  if (!isStreaming) {
    // Already stopped — return whatever we have.
    return Promise.resolve(buildCombinedTranscript());
  }
  isStreaming = false;

  // Stop capturing audio right away (so no new chunks are sent)...
  if (processor) { processor.disconnect(); processor.onaudioprocess = null; processor = null; }
  if (source) { source.disconnect(); source = null; }
  if (mediaStream) { mediaStream.getTracks().forEach(t => t.stop()); mediaStream = null; }

  // ...tell the server to finalize.
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ event: "stop" }));
  }

  // Wait up to 1.2s for the final transcription message, then tear down.
  return new Promise((resolve) => {
    const finish = () => {
      const text = buildCombinedTranscript();
      // Close audio context + socket after we've captured the final text.
      if (audioContext) { try { audioContext.close(); } catch (e) { /* noop */ } audioContext = null; }
      if (socket) { try { socket.close(); } catch (e) { /* noop */ } socket = null; }
      resolve(text);
    };
    pendingFinalResolve = () => { pendingFinalResolve = null; finish(); };
    pendingFinalTimer = setTimeout(() => {
      pendingFinalResolve = null;
      pendingFinalTimer = null;
      finish();
    }, 1200);
  });
}
